import { useState, type ReactNode } from "react";
import { LogOut, MailCheck, RefreshCw } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { useAuth } from "@/lib/auth/context";
import { toast } from "@/components/shell/Toaster";

const FAKE_EMAIL_SUFFIX = "kids.sianko.local";

/**
 * Hard-gate dla nieverified rodziców z prawdziwym emailem.
 *
 * Reguły blokady:
 *  - rodzic (role=parent)
 *  - email NIE jest fake-emailem dziecka (`@kids.sianko.local`)
 *  - email NIE jest tylko z Google (Google daje verified=true od razu)
 *  - emailVerified = false
 *
 * Powód: jeśli rodzic zapomni hasła, jedyny sposób recovery to link
 * resetu na zweryfikowany email. Bez tego = utracone konto + saldo dzieci.
 *
 * Dziecko (child) i Google-only rodzic pomijają gate całkowicie.
 */
export function EmailVerifiedGate({ children }: { children: ReactNode }) {
  const { firebaseUser, userDoc, sendVerificationEmail, signOutAll } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!firebaseUser || !userDoc) return <>{children}</>;
  if (userDoc.role !== "parent") return <>{children}</>;
  if (firebaseUser.emailVerified) return <>{children}</>;
  if (!firebaseUser.email) return <>{children}</>;
  if (firebaseUser.email.endsWith(`@${FAKE_EMAIL_SUFFIX}`)) {
    // Defense-in-depth: rodzic nie powinien mieć fake-emaila, ale gdyby
    // ktoś nakombinował (testy, migracja) — przepuść.
    return <>{children}</>;
  }
  const googleOnly = firebaseUser.providerData.every(
    (p) => p.providerId === "google.com",
  );
  if (googleOnly) return <>{children}</>;

  const handleResend = async () => {
    setBusy(true);
    try {
      await sendVerificationEmail();
      toast.success(`Link wysłany na ${firebaseUser.email}`);
    } catch (e) {
      const fbErr = e as { code?: string; message?: string };
      if (fbErr?.code === "auth/too-many-requests") {
        toast.error("Za szybko — odczekaj kilka minut.");
      } else {
        toast.error(fbErr?.message ?? "Błąd wysyłki");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-dvh flex flex-col bg-bg text-ink">
      <div className="mx-auto w-full max-w-md px-4 pt-12 pb-6 flex-1 flex flex-col">
        <div className="flex flex-col items-center text-center mb-6">
          <Logo variant="mark" size={56} />
          <h1 className="mt-4 text-xl font-semibold tracking-tight">
            Potwierdź email
          </h1>
        </div>
        <Card>
          <CardBody className="pt-5 space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-[12px] bg-pending-soft flex items-center justify-center shrink-0">
                <MailCheck className="h-5 w-5 text-pending" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">
                  Wysłaliśmy link weryfikacyjny na{" "}
                  <strong>{firebaseUser.email}</strong>
                </p>
                <p className="text-xs text-muted mt-1">
                  Kliknij link w mailu, wróć tutaj i naciśnij „Sprawdziłem".
                  Bez weryfikacji nie ma jak odzyskać hasła w razie zapomnienia.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="secondary"
                onClick={handleResend}
                disabled={busy}
              >
                {busy ? "…" : "Wyślij ponownie"}
              </Button>
              <Button
                variant="accent"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="h-4 w-4" />
                Sprawdziłem
              </Button>
            </div>
            <p className="text-[11px] text-muted text-center">
              Sprawdź też folder spam — Firebase wysyła z noreply@…firebaseapp.com.
            </p>
            <Button
              variant="ghost"
              size="sm"
              block
              onClick={async () => {
                await signOutAll();
                window.location.href = "/login";
              }}
            >
              <LogOut className="h-3.5 w-3.5" /> Wyloguj się
            </Button>
          </CardBody>
        </Card>
      </div>
    </main>
  );
}
